import { AlertTriangle } from "lucide-react";
import { Alert, Button, Modal } from "@/components/common";
import { useDeleteRole } from "@/hooks/useAdmin";
import type { Role } from "@/types/rbac";

interface DeleteRoleModalProps {
  role: Role | null;
  onClose: () => void;
}

export function DeleteRoleModal({ role, onClose }: DeleteRoleModalProps) {
  const deleteRole = useDeleteRole();

  function handleClose() {
    deleteRole.reset();
    onClose();
  }

  function handleConfirm() {
    if (!role) {
      return;
    }
    deleteRole.mutate(role.id, { onSuccess: handleClose });
  }

  const hasUsers = (role?.usersCount ?? 0) > 0;

  return (
    <Modal isOpen={role !== null} onClose={handleClose} title="Usuń rolę">
      <div className="space-y-4">
        {deleteRole.isError && (
          <Alert variant="error">
            {(deleteRole.error as { message?: string })?.message ??
              "Nie udało się usunąć roli"}
          </Alert>
        )}

        {hasUsers ? (
          <Alert variant="warning">
            Ta rola ma przypisanych {role?.usersCount} użytkowników. Najpierw
            przenieś ich do innej roli, zanim usuniesz tę rolę.
          </Alert>
        ) : (
          <div className="flex gap-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
            <AlertTriangle
              size={20}
              className="shrink-0 text-red-600 dark:text-red-400"
              aria-hidden="true"
            />
            <div className="text-sm text-red-800 dark:text-red-300">
              <p className="font-medium">Ta operacja jest nieodwracalna.</p>
              <p className="mt-1">
                Rola <span className="font-semibold">{role?.name}</span>{" "}
                zostanie trwale usunięta.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            isLoading={deleteRole.isPending}
            disabled={hasUsers}
          >
            Usuń rolę
          </Button>
        </div>
      </div>
    </Modal>
  );
}
